<template>
  <div class="container">
    <div class="form__wrapper" :class="{ 'drawer-open': actionDrawerSettings.visible }">
      <div class="form-prev">
        <AddFormElementCard v-if="activeInputs.length === 0" @open="handleAdd" />

        <a-form class="form" layout="vertical">
          <FormInputsList
            :form="dynamicForm"
            @click:add="handleAdd"
            @click:settings="handleSettings"
            @click:copy="handleCopy"
            @click:delete="handleDelete"
          />
        </a-form>

        <footer>
          <a-button
            type="primary"
            :disabled="isSaveDisabled"
            :loading="loading"
            @click="handleConfirmSave"
          >
            Guardar formulario
          </a-button>
        </footer>
      </div>
    </div>

    <Transition name="slide-fade">
      <FormInputActionDrawer
        v-if="actionDrawerSettings.visible"
        :action="actionDrawerSettings.action"
        :input="actionDrawerSettings.input"
        :form="activeInputs"
        @add="handleConfirm"
        @edit="handleEdit"
        @close="closeDrawer"
      />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, provide, inject, reactive, computed, watch } from "vue";
import type { Ref } from "vue";
import { useRoute } from "vue-router";
import { message, Modal } from "ant-design-vue";
import { cloneDeep, isEqual } from "lodash";

import {
  FormElementOption,
  FormElement,
  Form,
  DrawerAction,
  FormStateElementMap,
  ElementType,
  ElementStatus,
} from "@/@types/entities/forms";
import { DynamicForm } from "@/app/entities/utils/forms";
import { formService } from "@/app/entities/services";
import { ResponseError } from "@/app/shared/utils/httpManager";

import AddFormElementCard from "@/app/entities/components/card/AddFormElementCard.vue";
import FormInputActionDrawer from "@/app/entities/components/drawers/forms/FormInputActionDrawer.vue";
import FormInputsList from "@/app/entities/components/lists/FormInputsList.vue";

const emit = defineEmits<{
  (e: "update"): void;
  (e: "toggle:preview", enable: boolean): void;
}>();

const route = useRoute();

const formDetail = inject("formDetail") as Ref<Form | undefined>;

const dynamicForm = ref<FormElement[]>(cloneDeep(formDetail.value?.form) || []);
const formManager = new DynamicForm(dynamicForm.value);
const selectedInput = ref<FormElement | undefined>(undefined);

const isSaveDisabled = computed(() => isEqual(formDetail.value?.form, dynamicForm.value));
const activeInputs = computed(() =>
  dynamicForm.value.filter((input) => input.status === ElementStatus.Active)
);

provide("selectedInput", selectedInput);

watch(isSaveDisabled, () => emit("toggle:preview", isSaveDisabled.value));

/**
 * Action drawer
 */
interface ActionDrawer {
  visible: boolean;
  action: DrawerAction;
  input?: FormElement;
}

const actionDrawerSettings: ActionDrawer = reactive({
  visible: false,
  action: "add",
  input: undefined,
});

const openDrawer = (action: DrawerAction, input?: FormElement) => {
  actionDrawerSettings.visible = true;
  actionDrawerSettings.action = action;
  actionDrawerSettings.input = action === "add" ? undefined : input;
};

const closeDrawer = () => {
  actionDrawerSettings.visible = false;
  actionDrawerSettings.action = "add";
  actionDrawerSettings.input = undefined;

  selectedInput.value = undefined;
};

const scrollToElement = (name: string) => {
  const addedElement = document.getElementById(name);

  addedElement?.scrollIntoView({ block: "center", behavior: "smooth" });
};

/**
 * Popover actions
 */
const handleConfirm = (element: FormElementOption | undefined | null) => {
  if (!element) return;

  const newElementName = formManager.addElement(selectedInput.value?.name, element.type);

  closeDrawer();

  setTimeout(() => scrollToElement(newElementName), 400);
};

const handleAdd = () => {
  openDrawer("add");
};

const handleSettings = (input: FormElement) => {
  selectedInput.value = input;
  openDrawer("edit", selectedInput.value);
};

const handleEdit = (newElement: FormStateElementMap[ElementType]) => {
  if (!actionDrawerSettings.input) return;

  const newInput = { ...actionDrawerSettings.input, ...newElement };

  formManager.editElement(actionDrawerSettings.input?.name, newInput);
};

const handleCopy = (input: FormElement) => {
  // inputToManage.value = input;
  formManager.copyElement(input);
};

const handleDelete = (input: FormElement) => {
  // inputToManage.value = input;
  formManager.removeElement(input.name);
  selectedInput.value = undefined;
};

const loading = ref(false);

const handleSave = async () => {
  loading.value = true;

  try {
    const form = dynamicForm.value;

    if (!form) throw Error("No existe formulario");

    const newForm: Omit<Form, "id" | "conditionals"> = {
      name: formDetail.value?.name || "",
      description: formDetail.value?.description || "",
      form,
    };

    await formService.edit(Number(route.params.id), newForm);
    emit("update");

    message.success("Se ha guardado el formulario");
  } catch (error) {
    let errorMessage = "No se pudo guardar el formulario";

    if (error instanceof ResponseError) {
      console.error(error.data);
      errorMessage = error.getErrorMessage();
    }

    message.error(errorMessage);
  }

  loading.value = false;
};

const handleConfirmSave = () => {
  Modal.confirm({
    title: "¿Estás seguro de guardar los cambios realizados?",
    content: "La data histórica asociada a los campos editados o eliminados no se verá afectada",
    centered: true,
    onOk: async () => await handleSave(),
  });
};
</script>

<style scoped lang="sass">
.container
  display: flex
  justify-content: space-between
  gap: 12px
  height: 100%

.form
  overflow: auto
  max-height: calc(100vh - 268px)

.form__wrapper
  flex: 1 0 100%
  display: flex
  justify-content: center
  transition: flex-basis 0.3s ease

  &.drawer-open
    flex: 1 0 50%

.form-prev
  border-radius: 4px
  margin-top: 12px
  flex: 1 1 auto
  max-width: 50vw
  background-color: white
  position: relative
  height: fit-content
  overflow: hidden
  // max-height: calc(100vh - 130px)
  // overflow: auto

footer
  padding: 14px 12px
  border-top: 1px solid $gray-4
  text-align: right
  // position: sticky
  // bottom: 0
  background: white
  z-index: 3

h2
  font-size: 16px
  color: $gray-9
  font-weight: 400
  margin: 0

.slide-fade-enter-active
  transition: all 0.3s ease

.slide-fade-leave-active
  transition: all 0.3s ease

.slide-fade-enter-from, .slide-fade-leave-to
  // opacity: 0
  transform: translateX(100%)
</style>
